import React, { useState, useEffect, useCallback } from 'react'
import axios from 'axios'
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Modal,
  Select,
  Space,
  Tabs,
  Typography,
  message
} from 'antd'

import PartsAndMaterialsTable from '../components/PartsAndMaterialsTable.js'
import qs from 'qs'
import { fetchPricingConfig, getFormulaConstants, clearPricingConfigCache } from '../utils/pricingConfig'
import { getManufacturedParts } from '../calculators/manufactured-parts'

const { Title } = Typography

function calculateSuggestedPrice(inputData, buildingType, pricingConfig) {
  const parts = getManufacturedParts(inputData, buildingType, pricingConfig)
  let totalPartsCost = parts.reduce((a, b) => a + (b.cost || 0), 0)

  if (inputData['salt-fertilizer-building']) {
    const blackJack = parts.find(p => p.partNumber === '31555002')
    if (blackJack?.cost) totalPartsCost -= blackJack.cost
  }

  const sqft = Math.ceil((parseInt(inputData.buildingLength) * parseFloat(inputData.buildingWidthNumber)) || 0)
  const engineerDrawingsNeeded = inputData['engineer-sealed-drawings-needed-for-the-building-only']
  const { overhead, margin, markup, fee105 } = getFormulaConstants(pricingConfig)

  const basePrice = Math.ceil(
    Math.ceil((
      (totalPartsCost / overhead) +
      (engineerDrawingsNeeded ? 1 : 0) * (
        1250 +
        (sqft > 4500 ? 750 : 0) +
        (sqft > 8000 ? 500 : 0) +
        (sqft > 12000 ? 500 : 0) +
        (sqft > 15000 ? 500 : 0) +
        (sqft > 20000 ? 500 : 0) +
        (sqft > 25000 ? 1500 : 0)
      ) +
      fee105 * (
        (buildingType === 'advantage' ? 1 : 0) +
        (buildingType === 'integrity' ? Math.ceil(sqft / 3000) + (inputData.solidEndwallKit || 0) : 0) +
        (buildingType === 'integrity-cattle' ? Math.ceil(sqft / 3000) + (inputData.doorEndwallKit || 0) : 0)
      )
    ) / margin) / 10
  ) * 10

  return parseFloat(((basePrice + (inputData['salt-fertilizer-building'] ? 200 : 0)) * markup).toFixed(2))
}

const FORMULA_FIELDS_KEYS = ['galvRate', 'overhead', 'margin', 'markup', 'fee105']

function DraftTester({ buildingType, draft }) {
  const [estimates, setEstimates] = useState([])
  const [selectedEstimate, setSelectedEstimate] = useState(null)
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [formulaDraft, setFormulaDraft] = useState(null)
  const [includeFormula, setIncludeFormula] = useState(false)

  // Fetch formula draft once on mount
  useEffect(() => {
    axios.get('/api/pricing/formula/draft', { withCredentials: true })
      .then(r => setFormulaDraft(r.data))
      .catch(() => {})
  }, [])

  useEffect(() => {
    setSelectedEstimate(null)
    setResult(null)
    const types = buildingType === 'integrity' ? ['integrity', 'integrity-cattle'] : [buildingType]
    const query = qs.stringify({ buildingType: types, limit: 200 }, { arrayFormat: 'brackets' })
    axios.get(`/api/estimates?${query}`, { withCredentials: true })
      .then(res => {
        const all = Array.isArray(res.data) ? res.data : []
        all.sort((a, b) => new Date(b.modifiedDate) - new Date(a.modifiedDate))
        setEstimates(all)
      }).catch(err => console.error('DraftTester error:', err))
  }, [buildingType])

  // Re-run test whenever draft prices or formula inclusion changes
  useEffect(() => {
    if (selectedEstimate) runTest(selectedEstimate)
  }, [draft, includeFormula])

  const runTest = async (estimate) => {
    if (!estimate) return
    setLoading(true)
    setResult(null)
    try {
      const estType = estimate.inputData?.buildingType
      const configType = estType === 'integrity-cattle' ? 'integrity' : estType
      const liveConfig = await fetchPricingConfig(configType)
      const draftConfig = JSON.parse(JSON.stringify(liveConfig))

      if (draft?.materials?.length) {
        draft.materials.forEach(dm => {
          const m = draftConfig.materials.find(m => m.materialId === dm.materialId)
          if (m) {
            if (dm.pricePerPound !== null) m.pricePerPound = dm.pricePerPound
            if (dm.pricePerUnit !== null) m.pricePerUnit = dm.pricePerUnit
          }
        })
      }
      if (draft?.parts?.length) {
        draft.parts.forEach(dp => {
          const p = draftConfig.parts.find(p => p.partId === dp.partId)
          if (p) {
            if (dp.fastener !== null) p.fastener = dp.fastener
            if (dp.cover !== null) p.cover = dp.cover
          }
        })
      }
      if (includeFormula && formulaDraft) {
        FORMULA_FIELDS_KEYS.forEach(f => {
          if (formulaDraft[f] !== null && formulaDraft[f] !== undefined) {
            draftConfig.formula[f] = formulaDraft[f]
          }
        })
      }
      const inputData = estimate.inputData || {}
      const livePrice = calculateSuggestedPrice(inputData, estType, liveConfig)
      const draftPrice = calculateSuggestedPrice(inputData, estType, draftConfig)
      setResult({ livePrice, draftPrice, diff: draftPrice - livePrice })
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const fmt = v => v.toLocaleString('en-US', { style: 'currency', currency: 'USD' })

  const pct = result ? ((result.draftPrice - result.livePrice) / result.livePrice) * 100 : null
  const pctSign = pct > 0 ? '+' : ''

  return (
    <Card size="small" title="Draft Price Tester" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <Select
          showSearch
          placeholder="Estimate Name"
          style={{ width: 260 }}
          value={selectedEstimate?.id ?? undefined}
          loading={loading}
          onChange={id => {
            const est = estimates.find(x => x.id === id)
            setSelectedEstimate(est)
            runTest(est)
          }}
          filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
          options={estimates.map(e => ({
            value: e.id,
            label: `${e.title || '(Untitled)'}${e.customerName ? ` — ${e.customerName}` : ''}`
          }))}
        />

        {formulaDraft && (
          <div style={{ borderLeft: '1px solid #e8e8e8', paddingLeft: 16, ...(selectedEstimate ? { borderRight: '1px solid #e8e8e8', paddingRight: 16 } : {}) }}>
            <Checkbox
              checked={includeFormula}
              onChange={e => setIncludeFormula(e.target.checked)}
            >
              <span style={{ fontSize: 13 }}>Include formula draft</span>
            </Checkbox>
          </div>
        )}

        {result && (
          <div style={{ display: 'flex', gap: 40, animation: 'fadeSlideIn 0.2s ease' }}>
            <div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>Previous Price</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: '#666667', fontVariantNumeric: 'tabular-nums' }}>{fmt(result.livePrice)}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 2 }}>Draft Price</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: '#000A1B', fontVariantNumeric: 'tabular-nums' }}>{fmt(result.draftPrice)}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, color: pct > 0 ? '#23672A' : '#9C1937', marginBottom: 2 }}>Change</div>
              <div style={{ fontSize: 22, fontWeight: 600, color: pct > 0 ? '#23672A' : '#9C1937', fontVariantNumeric: 'tabular-nums' }}>
                {pctSign}{pct.toFixed(2)}%
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}


export default function PartsAndMaterials({ buildingType }) {
  const [draft, setDraft] = useState(null)
  const [hasSnapshot, setHasSnapshot] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const [publishing, setPublishing] = useState(false)
  const [discarding, setDiscarding] = useState(false)
  const [rollingBack, setRollingBack] = useState(false)

  const triggerRefresh = () => setRefreshKey(k => k + 1)

  const fetchDraft = useCallback(async () => {
    try {
      const res = await axios.get(`/api/pricing/draft/${buildingType}`, { withCredentials: true })
      setDraft(res.data)
    } catch {}
  }, [buildingType])

  const fetchSnapshot = useCallback(async () => {
    try {
      const res = await axios.get(`/api/pricing/snapshot/${buildingType}`, { withCredentials: true })
      setHasSnapshot(res.data.exists)
    } catch { setHasSnapshot(false) }
  }, [buildingType])

  useEffect(() => {
    fetchDraft()
    fetchSnapshot()
  }, [fetchDraft, fetchSnapshot])

  const publish = async () => {
    setPublishing(true)
    try {
      await axios.post(`/api/pricing/draft/publish/${buildingType}`, {}, { withCredentials: true })
      clearPricingConfigCache()
      setDraft(null)
      triggerRefresh()
      fetchSnapshot()
      message.success('Published successfully')
    } catch {
      message.error('Failed to publish')
    } finally {
      setPublishing(false)
    }
  }

  const discard = () => {
    Modal.confirm({
      title: 'Discard draft?',
      content: 'All unpublished material and part price changes will be lost.',
      okText: 'Discard',
      okType: 'danger',
      onOk: async () => {
        setDiscarding(true)
        try {
          await axios.delete(`/api/pricing/draft/${buildingType}`, { withCredentials: true })
          clearPricingConfigCache()
          setDraft(null)
          triggerRefresh()
          message.success('Draft discarded')
        } catch {
          message.error('Failed to discard')
        } finally {
          setDiscarding(false)
        }
      }
    })
  }

  const rollback = () => {
    Modal.confirm({
      title: 'Roll back to last published state?',
      content: `This will revert live pricing for ${buildingType === 'advantage' ? 'Advantage' : 'Integrity'} to the previous publish.`,
      okText: 'Roll Back',
      okType: 'danger',
      onOk: async () => {
        setRollingBack(true)
        try {
          await axios.post(`/api/pricing/rollback/${buildingType}`, {}, { withCredentials: true })
          clearPricingConfigCache()
          await Promise.all([fetchDraft(), fetchSnapshot()])
          triggerRefresh()
          message.success('Rolled back successfully')
        } catch {
          message.error('Failed to roll back')
        } finally {
          setRollingBack(false)
        }
      }
    })
  }

  const hasDraft = !!draft

  const draftSummary = (() => {
    const mCount = draft?.materials?.length ?? 0
    const pCount = draft?.parts?.length ?? 0
    return [
      mCount ? `${mCount} material${mCount !== 1 ? 's' : ''}` : null,
      pCount ? `${pCount} part${pCount !== 1 ? 's' : ''}` : null
    ].filter(Boolean).join(' · ')
  })()

  const label = buildingType === 'advantage' ? 'Advantage' : 'Integrity'

  return (
    <>
      <Title level={2}>{label} — Parts & Materials</Title>

      <div id="parts-materials">
        {(hasDraft || hasSnapshot) && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={hasDraft ? 'Unpublished changes' : 'Last publish can be rolled back'}
            description={hasDraft ? draftSummary : undefined}
            action={
              <Space>
                {hasDraft && <>
                  <Button size="small" type="primary" loading={publishing} onClick={publish}>
                    Publish
                  </Button>
                  <Button size="small" danger loading={discarding} onClick={discard}>
                    Discard
                  </Button>
                </>}
                {hasSnapshot && (
                  <Button size="small" loading={rollingBack} onClick={rollback}>
                    Roll Back
                  </Button>
                )}
              </Space>
            }
          />
        )}

        {hasDraft && (
          <DraftTester
            buildingType={buildingType}
            draft={draft}
          />
        )}

        <Tabs
          defaultActiveKey="materials"
          size="small"
          items={[
            {
              key: 'materials',
              label: 'Materials',
              children: (
                <PartsAndMaterialsTable
                  dataType='materials'
                  buildingType={buildingType}
                  refreshKey={refreshKey}
                  onDraftChange={fetchDraft}
                  draft={draft}
                />
              )
            },
            {
              key: 'parts',
              label: 'Parts',
              children: (
                <PartsAndMaterialsTable
                  dataType='parts'
                  buildingType={buildingType}
                  refreshKey={refreshKey}
                  onDraftChange={fetchDraft}
                  draft={draft}
                />
              )
            }
          ]}
        />
      </div>
    </>
  )
}
